import { useMsal } from "@azure/msal-react";
import {
  type HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

import { apiScopes } from "../../auth/authConfig";
import { useSnackbar } from "../../hooks/useSnackbar";
import { queryKeys } from "../queryKeys";
import { getHubUrl } from "./useSignalR";

const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === "true";

export interface SignalRContextValue {
  joinCase: (caseId: string) => Promise<void>;
  leaveCase: (caseId: string) => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const SignalRContext = createContext<SignalRContextValue>({
  joinCase: async () => {},
  leaveCase: async () => {},
});

export function SignalRProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();
  // eslint-disable-next-line react-hooks/rules-of-hooks -- AUTH_BYPASS is a build-time constant, never changes at runtime
  const msal = AUTH_BYPASS ? { instance: null, accounts: [] } : useMsal();
  const { instance, accounts } = msal as ReturnType<typeof useMsal>;
  const connectionRef = useRef<HubConnection | null>(null);

  const invalidateCases = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.cases });
  }, [queryClient]);

  useEffect(() => {
    if (AUTH_BYPASS || accounts.length === 0) return;

    let cancelled = false;

    const connect = async () => {
      try {
        const connection = new HubConnectionBuilder()
          .withUrl(getHubUrl(), {
            accessTokenFactory: async () => {
              const tokenResponse = await instance.acquireTokenSilent({
                ...apiScopes,
                account: accounts[0],
              });
              return tokenResponse.accessToken;
            },
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
          .configureLogging(LogLevel.Warning)
          .build();

        connection.on("CaseUpdated", (caseId: string) => {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.case(caseId),
          });
          invalidateCases();
          showSnackbar(t("notification.caseUpdated"), "info");
        });

        connection.on("CommentAdded", (caseId: string) => {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.case(caseId),
          });
          showSnackbar(t("notification.commentAdded"), "info");
        });

        connection.on(
          "CaseAssigned",
          (_caseId: string, _caseNumber: string, groupName: string) => {
            invalidateCases();
            showSnackbar(
              t("notification.caseAssigned", { group: groupName }),
              "info",
            );
          },
        );

        connection.on("AttachmentChanged", (caseId: string) => {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.case(caseId),
          });
        });

        if (!cancelled) {
          await connection.start();
          connectionRef.current = connection;
        }
      } catch (err) {
        console.warn("SignalR connection failed:", err);
      }
    };

    void connect();

    return () => {
      cancelled = true;
      void connectionRef.current?.stop();
    };
  }, [accounts, instance, queryClient, invalidateCases, showSnackbar, t]);

  const joinCase = useCallback(async (caseId: string) => {
    await connectionRef.current?.invoke("JoinCaseChannel", caseId);
  }, []);

  const leaveCase = useCallback(async (caseId: string) => {
    await connectionRef.current?.invoke("LeaveCaseChannel", caseId);
  }, []);

  return (
    <SignalRContext.Provider value={{ joinCase, leaveCase }}>
      {children}
    </SignalRContext.Provider>
  );
}
